import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import { margin, marginSizes } from '@jarvis.oddle/core/theme/default'
import {
  findItemFromMenu,
  getOptionsMinimumQuantity,
  status
} from '@jarvis.oddle/core/tools/product'
import modalController, {
  makeRouteControlledModal
} from 'src/decorators/ModalController'
import Modal from '@jarvis.oddle/core/components/Modal'
import toast from '@jarvis.oddle/core/components/Toast'
import Accordion from '@jarvis.oddle/core/components/Accordion'
import Error, { errorTypes } from '@jarvis.oddle/core/components/Error'
import {
  ProductBody,
  ProductCloseButton,
  ProductDetailsContainer,
  ProductFlexContainer,
  ProductFooter,
  ProductForm,
  ProductState,
  ProductWrapper
} from '@jarvis.oddle/core/molecules/ProductDetailsModal'
import { getOrderItem } from 'src/utils/product'
import { makeQueryString, redirect } from 'src/utils/routes'
import pickUpDeliveryFlow from 'src/flows/pickUpDeliveryFlow'
import AddToCartToast from 'src/components/molecules/AddToCartToast'
import ProductImageState from 'src/components/molecules/ProductImageState'
import ProductImage from './ProductImage'
import ProductDescription from './ProductDescription'
import ProductVariant from './ProductVariant'
import ProductOption from './ProductOption'
import AddToCart from './AddToCart'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import useAppSelector from 'src/hooks/useAppSelector'
import useAppDispatch from 'src/hooks/useAppDispatcher'
import { useRouter } from 'next/router'
import { addItemToCartAction } from 'src/redux/order/action'
import { isEmpty } from 'src/utils/string'
import { selectMenuProductList } from 'src/redux/menu'
import useTracker from 'src/hooks/useTracker'
import { selectMenuMode } from 'src/redux/meta'

const SUB_ITEM_VARIANTS_KEY = 'variants'

const defaultProductState: {
  id: string
  name: string
  variants: Menu.Variant[]
  subitems: Menu.Subitems[]
} = {
  id: '',
  name: '',
  variants: [],
  subitems: []
}

const StyledShopLink = styled.a`
  margin-top: ${margin[marginSizes.medium]};
`

interface ProductDetailsModalProps {
  modal: {
    show: boolean
    onClose: (callback: () => void) => void
    onClosed: () => void
  }
  id?: string
}

const ProductDetailsModal = ({
  modal,
  id
}: ProductDetailsModalProps): JSX.Element => {
  const router = useRouter()
  const dispatch = useAppDispatch()
  const { data: menu } = useAppSelector(selectMenuProductList)
  const { notifyAddToCart } = useTracker()
  const menuMode = useAppSelector(selectMenuMode)

  const productId = id || (router.query.productId as string)
  const productAvailability = findItemFromMenu({
    menu,
    id: productId
  }) as Menu.MenuItem
  const category =
    menu?.find((c) => c.items.includes(productAvailability)) ||
    ({} as Menu.MenuData)
  const isProductNotFound: boolean = isEmpty(productId) && !productAvailability

  const { t } = useContext(TranslationContext)
  const [product, setProduct] = useState<
    | Menu.MenuItem
    | {
        id: string
        variants: Menu.Variant[]
        name: string
        subitems: Menu.Subitems[]
      }
  >(defaultProductState)
  const [productState, setProductState] = useState(defaultProductState)
  const [totalItemPrice, setTotalItemPrice] = useState<number>(0)
  const [preselectedItem, setPreselectedItem] = useState<null | string>(null)
  const landingPageUrl = router.query.slug
    ? '/[locale]/menus/[slug]/'
    : '/[locale]/'

  const asHref = router.query.slug
    ? `/${router.query.locale}/menus/${router.query.slug}/`
    : `/${router.query.locale}/`

  // code to implement getDerivedStateFromProps from fesc/packages/components/molecules/productDetailsModal/productDetailsModal.js
  if (productAvailability) {
    if (modal.show && productAvailability !== product) {
      setProduct(productAvailability)
      setPreselectedItem(
        (productAvailability.variants || []).length > 1
          ? productAvailability.id
          : (productAvailability.subitems || []).length > 0
          ? productAvailability.subitems[0].id
          : null
      )
    }
  }

  useEffect(() => {
    if (productId && modal.show) {
      window.onpopstate = () => {
        window.location.href = asHref
      }
    }
  }, [modal.show, productId, asHref])

  // Reset product state when modal closed
  const onClose = (callBack): void => {
    if (id) {
      router.push(landingPageUrl, asHref, { shallow: true })
    }

    modal.onClose(() => {
      setProduct(defaultProductState)
      typeof callBack === 'function' && callBack()
    })
  }

  const onFormSubmit = async (
    values,
    { setSubmitting, setErrors, setStatus },
    _product,
    controlled
  ) => {
    if (menuMode === 'preview') {
      modalController.showModal('ErrorModal', {
        error: t('Cannot add item to cart on preview mode')
      })
      setSubmitting(false)
      return
    }
    const product = _product || productState
    const unavailable = product && product.status === status.UNAVAILABLE

    const optionsMinimumQuantity = getOptionsMinimumQuantity({
      product,
      selectedVariant: values.selectedVariant
    })

    const invalidOption = Object.keys(optionsMinimumQuantity).find((key) => {
      return (
        (optionsMinimumQuantity[key] > 0 &&
          (values.subitems || {})[key] === undefined) ||
        optionsMinimumQuantity[key] >
          Object.keys((values.subitems || {})[key] || {}).reduce(
            (sum, item) => sum + values.subitems[key][item],
            0
          )
      )
    })

    setStatus('touched')

    if (invalidOption) {
      const optionElement = window.document.getElementById(invalidOption)
      if (optionElement) {
        optionElement.scrollIntoView({ behavior: 'smooth' })
      }
    } else {
      const action = controlled ? (fn) => fn() : onClose
      action(async () => {
        if (
          !controlled &&
          (unavailable || !pickUpDeliveryFlow.getMeta().isVisited)
        ) {
          pickUpDeliveryFlow.start({
            onFinished: () =>
              onFormSubmit(
                values,
                { setSubmitting, setErrors, setStatus },
                product,
                true
              )
          })
        } else {
          const addItem = await dispatch(
            addItemToCartAction({
              orderItem: getOrderItem(
                product,
                values.totalQuantity,
                values.selectedVariant,
                values.subitems
              ),
              menuItem: product,
              finalPrice: totalItemPrice
            })
          )

          if (
            addItem.meta.requestStatus === 'fulfilled' &&
            isEmpty(addItem.payload.payload.error)
          ) {
            notifyAddToCart(addItem.payload)

            const items = [
              {
                name: product.name,
                quantity: values.totalQuantity,
                price: totalItemPrice,
                imageUrl: product.imageFullUrls[0] || product.imageUrls?.[0]
              }
            ]

            toast(<AddToCartToast items={items} />, {
              slideInAnimation: 'top',
              closeOnPageClick: true
            })
          } else {
            modalController.showModal('ErrorModal', {
              error: t(addItem.payload.payload.error)
            })
          }
        }
      })
    }

    setSubmitting(false)
  }

  const onProductStateMount = ({ productState, totalPrice }): void => {
    setProductState(productState)
    setTotalItemPrice(totalPrice)
  }

  const onProductStateChange = (_, { productState, totalPrice }): void => {
    setProductState(productState)
    setTotalItemPrice(totalPrice)
  }

  const hasVariantChoices = product.variants.length > 1
  const subitemCount = product.subitems.length
  const isSimpleItem = !hasVariantChoices && !subitemCount
  const initialFocus = isProductNotFound ? null : '#totalQuantity'

  return (
    <Modal
      {...modal}
      withoutFocusTrap
      onClose={onClose}
      initialFocus={initialFocus}
      slideInAnimation='bottom'
      show={modal.show && !isEmpty(productId)}
    >
      {isProductNotFound ? (
        <Error
          type={errorTypes.productNotFound}
          LinkComponent={StyledShopLink}
        />
      ) : (
        <ProductForm
          initialValues={{ totalQuantity: 1 }}
          onSubmit={onFormSubmit}
        >
          <ProductState
            product={product}
            subItemVariantsKey={SUB_ITEM_VARIANTS_KEY}
            onMount={onProductStateMount}
            onChange={onProductStateChange}
          >
            {({
              productState,
              totalQuantity,
              totalPrice,
              calculateOptionDiff,
              selectedVariantId,
              calculateVariantDiff
            }) => (
              <ProductFlexContainer
                maxHeight={isSimpleItem ? '580px' : undefined}
              >
                <ProductCloseButton onClick={onClose} />
                <ProductWrapper>
                  <ProductDetailsContainer>
                    <ProductImageState>
                      {({ showImage, showDefaultImage }) => (
                        <React.Fragment>
                          {showImage && (
                            <ProductImage
                              productState={productState}
                              showDefaultImage={showDefaultImage}
                              showImage={showImage}
                            />
                          )}
                          <ProductDescription
                            productState={productState}
                            category={category}
                            showImage={showImage}
                          />
                        </React.Fragment>
                      )}
                    </ProductImageState>
                  </ProductDetailsContainer>
                  {!isSimpleItem && (
                    <ProductBody>
                      <Modal.Section>
                        <Accordion
                          showOnMount={preselectedItem}
                          mode={Accordion.modes.constantlySingle}
                        >
                          {({ sectionList }) => (
                            <React.Fragment>
                              {hasVariantChoices && (
                                <ProductVariant
                                  subItemVariantsKey={SUB_ITEM_VARIANTS_KEY}
                                  variants={productState.variants}
                                  id={productState.id}
                                  selectedVariantId={selectedVariantId}
                                  calculateVariantDiff={calculateVariantDiff}
                                  calculateOptionDiff={calculateOptionDiff}
                                  hideArrow={sectionList.length <= 1}
                                />
                              )}
                              {subitemCount > 0 &&
                                productState.subitems.map((option) => (
                                  <ProductOption
                                    subItemVariantsKey={SUB_ITEM_VARIANTS_KEY}
                                    option={option}
                                    hideArrow={sectionList.length <= 1}
                                    calculateOptionDiff={calculateOptionDiff}
                                  />
                                ))}
                            </React.Fragment>
                          )}
                        </Accordion>
                      </Modal.Section>
                    </ProductBody>
                  )}
                </ProductWrapper>
                <ProductFooter data-testid='productDetailsModal.footer'>
                  <AddToCart
                    category={category}
                    productState={productState}
                    totalQuantity={totalQuantity}
                    totalItemPrice={totalPrice}
                  />
                </ProductFooter>
              </ProductFlexContainer>
            )}
          </ProductState>
        </ProductForm>
      )}
    </Modal>
  )
}

export default makeRouteControlledModal(
  ProductDetailsModal,
  'ProductDetailsModal',
  ({ pathname, query }) =>
    (pathname === '/[locale]' || pathname === '/[locale]/menus/[slug]') &&
    !isEmpty(query.productId),
  (router) => {
    const { locale, slug, previewMode, previewToken } = router.query
    redirect({
      url: slug ? '/[locale]/menus/[slug]' : '/[locale]',
      as: slug
        ? `/${locale}/menus/${slug}${makeQueryString({
            previewMode,
            previewToken
          })}`
        : `/${locale}${makeQueryString({
            previewMode,
            previewToken
          })}`,
      scroll: false,
      shallow: true
    })
  }
)
